import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import { eventChannel } from 'redux-saga';

import { event } from '@xanadu/ui-shared/modules/schema';

const DEFAULT_FAKE_PREDICTIONS_START = '2026-05-01T07:28:07.283Z';
const DEFAULT_FAKE_PREDICTIONS_LAST_PRICE_UPDATE = '2026-01-05T16:22:19.500Z';
const DEFAULT_FAKE_PREDICTIONS_SPORT = {
  id: '15',
  name: 'Soccer',
  urlName: 'soccer',
};

const DEFAULT_FAKE_PREDICTIONS_COMPETITION = {
  id: '2505340085650013',
  name: 'Premier League',
  urlName: 'premier-league',
};

const DEFAULT_FAKE_PREDICTIONS_EVENT_DEFINITIONS = [
  {
    eventName: 'Will Rory McIlroy Win Masters 2027',
    runnerPriceA: 11,
    runnerPriceB: 90,
    isTrendingEvent: true
  },
  {
    eventName: 'Will Reform win the most parliamentary seats at the next UK general election',
    runnerPriceA: 44,
    runnerPriceB: 57,
    sportName: 'Politics',
  },
  {
    eventName: 'Will Chiefs win the Superbowl',
    runnerPriceA: 7,
    runnerPriceB: 94,
    sportName: 'American Football',
  },
  {
    eventName: 'Will reform be largest party after next general election',
    runnerPriceA: 45,
    runnerPriceB: 56,
    sportName: 'Politics',
  },
];

function getFakePredictionsId(entityType, index) {
  if (index === 0) {
    return `ob-wizard-predictions-${entityType}-id`;
  }

  return `ob-wizard-predictions-${entityType}-id-${index + 1}`;
}

function getFakePredictionsParticipantId(index) {
  return `ob-wizard-predictions-ep-id-${index + 1}`;
}

export function buildFakePredictionsEventModel(fakePredictionsEventDefinitions = []) {
  const fakeEvents = fakePredictionsEventDefinitions.map((fakePredictionEventDefinition, index) => {
    const {
      eventName,
      runnerPriceA,
      runnerPriceB,
      sportId = DEFAULT_FAKE_PREDICTIONS_SPORT.id,
      sportName = DEFAULT_FAKE_PREDICTIONS_SPORT.name,
      sportUrlName = DEFAULT_FAKE_PREDICTIONS_SPORT.urlName,
      competitionId = DEFAULT_FAKE_PREDICTIONS_COMPETITION.id,
      competitionName = DEFAULT_FAKE_PREDICTIONS_COMPETITION.name,
      competitionUrlName = DEFAULT_FAKE_PREDICTIONS_COMPETITION.urlName,
      start = DEFAULT_FAKE_PREDICTIONS_START,
      isTrendingEvent = false
    } = fakePredictionEventDefinition;

    const eventId = getFakePredictionsId('event', index);
    const marketId = getFakePredictionsId('market', index);
    const runnerId = getFakePredictionsId('runner', index);
    const yesParticipantId = getFakePredictionsParticipantId(index * 2);
    const noParticipantId = getFakePredictionsParticipantId(index * 2 + 1);
    const trendingId = '33106529125500047';

    return {
      id: eventId,
      name: eventName,
      'sport-id': sportId,
      start,
      'in-running-flag': false,
      'allow-live-betting': false,
      'category-id': [sportId, competitionId, ...(isTrendingEvent ? [trendingId] : [])],
      status: 'open',
      volume: 0.0,
      'event-participants': [
        {
          id: yesParticipantId,
          'event-id': eventId,
          'participant-name': eventName,
        },
        {
          id: noParticipantId,
          'event-id': eventId,
          'participant-name': 'No Winner',
        },
      ],
      markets: [
        {
          live: false,
          'event-id': eventId,
          id: marketId,
          name: eventName,
          product: 'PREDICTIONS',
          start,
          status: 'open',
          'market-type': 'place',
          type: 'outright',
          'in-running-flag': false,
          'allow-live-betting': false,
          volume: 0.0,
          'number-of-winners': 1,
          'top-market': true,
          runners: [
            {
              withdrawn: false,
              prices: [
                {
                  'available-amount': 675.0,
                  currency: 'GBP',
                  'odds-type': 'PROBABILITY',
                  odds: runnerPriceA,
                  'decimal-odds': 2.22,
                  side: 'win',
                  'exchange-type': 'binary',
                },
                {
                  'available-amount': 482.0,
                  currency: 'GBP',
                  'odds-type': 'PROBABILITY',
                  odds: runnerPriceB,
                  'decimal-odds': 2.13,
                  side: 'lose',
                  'exchange-type': 'binary',
                },
              ],
              'last-price-update-time': DEFAULT_FAKE_PREDICTIONS_LAST_PRICE_UPDATE,
              'event-id': eventId,
              id: runnerId,
              'market-id': marketId,
              name: eventName,
              status: 'open',
              volume: 0.0,
              'event-participant-id': yesParticipantId,
            },
          ],
        },
      ],
      'meta-tags': [
        {
          id: sportId,
          name: sportName,
          type: 'SPORT',
          'url-name': sportUrlName,
        },
        ...(isTrendingEvent ? [{
          id: trendingId,
          name: 'Trending',
          type: 'OTHER',
          'url-name': 'trending',
        }] : [{
          id: competitionId,
          name: competitionName,
          type: 'COMPETITION',
          'url-name': competitionUrlName,
        }])
      ],
    };
  });

  const { entities } = normalize(camelizeKeys(fakeEvents), [event]);

  return {
    ...entities,
    status: 'ready',
  };
}

export function createOneTrustChannel() {
  return eventChannel((emitter) => {
    const handler = () => {
      emitter({
        oneTrustBoxClosed: true
      });
    };

    if (window.cookieyes) {
      document.addEventListener('cookieyes_consent_update', handler);
    }

    return () => { };
  });
}

export function loadFakePredictionsEvent() {
  return Promise.resolve(
    buildFakePredictionsEventModel(DEFAULT_FAKE_PREDICTIONS_EVENT_DEFINITIONS)
  );
}
